import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { CheckCircle2, Circle, ClipboardList, Wrench, Package, ShoppingCart, FileText, ShieldCheck, Lock } from 'lucide-react';

/**
 * ComplaintWorkflowTimeline
 * ─────────────────────────
 * Compact 10-step chain rendered inside a complaint detail dialog. Steps:
 *   1. Complaint raised
 *   2. WO dispatched
 *   3. Material Request raised
 *   4. Engineer approved MR
 *   5. Materials issued (or PR raised)
 *   6. PR / PO approved (if any)
 *   7. GRN received (if any)
 *   8. WO Complete → SR generated
 *   9. Engineer verified SR
 *  10. Complaint closed
 *
 * State derivation is best-effort from the complaint object plus a light
 * fetch of linked MR / PR docs — no page-hopping needed.
 */
export default function ComplaintWorkflowTimeline({ complaint }) {
  const [linked, setLinked] = useState({ mrs: [], prs: [] });

  useEffect(() => {
    if (!complaint?.linked_wo_id) return;
    (async () => {
      try {
        const mrs = await axios.get(`${API}/material-requests`, { params: { work_order_id: complaint.linked_wo_id }, withCredentials: true }).then(r => r.data || []).catch(() => []);
        const prs = [];
        for (const mr of mrs) {
          for (const l of (mr.lines || [])) {
            if (l.pr_number) prs.push({ pr_number: l.pr_number, mr_id: mr.id, mr_number: mr.mr_number });
          }
        }
        setLinked({ mrs, prs });
      } catch (_) { setLinked({ mrs: [], prs: [] }); }
    })();
  }, [complaint?.linked_wo_id]);

  const hasWO = !!complaint?.linked_wo_id;
  const hasMR = linked.mrs.length > 0;
  const engineerApprovedMR = linked.mrs.some(m => ['approved', 'partially_issued', 'issued'].includes(m.status));
  const materialsIssued = linked.mrs.some(m => ['issued', 'partially_issued'].includes(m.status)) || complaint?.latest_report_status === 'materials_issued';
  const hasPR = linked.prs.length > 0;
  const srCompleted = ['completed', 'signed', 'approved'].includes(complaint?.latest_report_status);
  const engineerVerified = complaint?.latest_report_status === 'approved';
  const closed = complaint?.status === 'closed';

  const currentStep = (() => {
    if (closed) return 10;
    if (engineerVerified) return 10;
    if (srCompleted) return 9;
    if (materialsIssued) return 8;
    if (hasPR) return 6;
    if (engineerApprovedMR) return 5;
    if (hasMR) return 4;
    if (hasWO) return 3;
    return 1;
  })();

  const steps = [
    { n: 1, label: 'Complaint raised', icon: FileText, done: true },
    { n: 2, label: 'WO dispatched', icon: ClipboardList, done: hasWO },
    { n: 3, label: 'Materials requested', icon: Wrench, done: hasMR },
    { n: 4, label: 'Engineer approved MR', icon: ShieldCheck, done: engineerApprovedMR },
    { n: 5, label: 'Materials issued', icon: Package, done: materialsIssued },
    { n: 6, label: 'PR / PO (if short-stock)', icon: ShoppingCart, done: hasPR, optional: !hasPR },
    { n: 7, label: 'GRN received', icon: Package, done: hasPR && materialsIssued, optional: !hasPR },
    { n: 8, label: 'WO completed → SR', icon: FileText, done: srCompleted },
    { n: 9, label: 'Engineer verified SR', icon: ShieldCheck, done: engineerVerified },
    { n: 10, label: 'Complaint closed', icon: Lock, done: closed },
  ];

  return (
    <div data-testid="complaint-workflow-timeline" className="rounded-lg border border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 p-3">
      <div className="text-[11px] uppercase tracking-wider text-teal-800 font-semibold mb-2 flex items-center gap-1.5">
        <ClipboardList className="w-3.5 h-3.5" /> Workflow — where this complaint is
      </div>
      <ol className="flex overflow-x-auto gap-1 pb-1">
        {steps.map((s) => {
          const isCurrent = s.n === currentStep && !s.done;
          const Icon = s.icon;
          const state = s.done ? 'done' : isCurrent ? 'current' : s.optional ? 'skipped' : 'pending';
          const color = {
            done:    'bg-emerald-500 text-white border-emerald-500',
            current: 'bg-teal-500 text-white border-teal-500 animate-pulse',
            pending: 'bg-white text-slate-400 border-slate-200',
            skipped: 'bg-white text-slate-300 border-slate-200 opacity-50',
          }[state];
          return (
            <li key={s.n} className="shrink-0 flex flex-col items-center gap-1 min-w-[74px]" title={`Step ${s.n} — ${s.label}${s.optional ? ' (optional)' : ''}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${color}`}>
                {s.done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <div className={`text-[10px] leading-tight text-center ${isCurrent ? 'font-bold text-teal-700' : s.done ? 'text-emerald-700' : 'text-slate-500'}`}>{s.label}</div>
            </li>
          );
        })}
      </ol>
      <div className="mt-2 flex items-center justify-between text-[10px] text-teal-800">
        <span>Step {currentStep} / 10</span>
        {linked.mrs.length > 0 && <span>{linked.mrs.length} MR{linked.mrs.length > 1 ? 's' : ''} · {linked.prs.length} PR{linked.prs.length === 1 ? '' : 's'}</span>}
      </div>
    </div>
  );
}
